import type { CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { Figure } from "../components/Figure";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { useScrollPastHero } from "../lib/useScrollPastHero";
import {
  SERVICE_WINGS,
  RED_CROSS_PRINCIPLES,
  getServiceWing,
  type ServiceWing as Wing,
} from "../lib/campusLife";

/** Card used both on /campus-life and for the sibling links at the foot here. */
export function ServiceWingCard({ w }: { w: Wing }) {
  return (
    <Link
      to={`${ROUTES.serviceWing}/${w.slug}`}
      className="sw-card reveal"
      style={{ ["--sw-accent" as string]: w.accent } as CSSProperties}
    >
      <div className="sw-card-media">
        <Figure
          media={w.cardPhoto}
          fallbackLabel={`${w.abbr} · ${w.name.toUpperCase()}`}
          aspect="4/3"
          sizes="(max-width: 640px) 100vw, (max-width: 1000px) 48vw, 31vw"
        />
        <span className="sw-card-abbr">{w.abbr}</span>
      </div>
      <div className="sw-card-body">
        <h3>{w.name}</h3>
        {w.motto && <div className="sw-card-motto">“{w.motto}”</div>}
        <p className="sw-card-strap">{w.strap}</p>
        <div className="sw-card-foot">
          <span className="sw-card-officer">
            <b>{w.officer.name}</b>
            {w.since ? `At BIET since ${w.since}` : "Programme officer"}
          </span>
          <span className="sw-card-more">
            Read more <span className="arr">→</span>
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function ServiceWing() {
  const { slug = "" } = useParams<{ slug: string }>();
  const w = getServiceWing(slug);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (!w) {
    return (
      <section className="section">
        <div className="container">
          <div className="kicker">404</div>
          <h1>Service wing not found.</h1>
          <p style={{ marginTop: 16 }}>
            <Link className="btn-link" to={ROUTES.campusLife}>
              ← Back to campus life
            </Link>
          </p>
        </div>
      </section>
    );
  }

  const accent = { ["--sw-accent" as string]: w.accent } as CSSProperties;
  const siblings = SERVICE_WINGS.filter((x) => x.slug !== w.slug);

  // The hero leads with this wing's card frame and the sibling cards at the
  // foot carry theirs, so the gallery takes what none of them has used. Filter
  // against every wing, not just this one: NSS borrows its card image from the
  // Red Cross set, which would otherwise show up twice on /red-cross.
  const onCards = new Set(SERVICE_WINGS.map((x) => x.cardPhoto.url));
  const rest = w.photos.filter((p) => !onCards.has(p.url));

  return (
    <article style={accent}>
      <ContextBar
        visible={scrolledPast}
        tag={w.abbr}
        title={w.name}
        back={{ label: "Campus life", to: ROUTES.campusLife }}
      />

      <section className="sw-hero" ref={heroRef} style={accent}>
        <div className="container">
          <div className="sw-hero-grid">
            <div>
              <nav className="event-crumbs" aria-label="Breadcrumb">
                <Link to={ROUTES.home}>Home</Link>
                <span aria-hidden>/</span>
                <Link to={ROUTES.campusLife}>Campus Life</Link>
                <span aria-hidden>/</span>
                <span>{w.abbr}</span>
              </nav>
              <div className="sw-hero-abbr">{w.abbr}</div>
              <h1>{w.name}</h1>
              {w.motto && <p className="sw-hero-motto">“{w.motto}”</p>}
              {w.since && <div className="sw-hero-since">At BIET since {w.since}</div>}
            </div>
            <div className="sw-hero-figure">
              <Figure
                media={w.cardPhoto}
                fallbackLabel={`${w.abbr} · ${w.name.toUpperCase()}`}
                aspect="4/3"
                kind="dark"
                sizes="(max-width: 700px) 100vw, 42vw"
                priority
              />
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="sw-officer">
          {w.officerPhoto && (
            <Figure
              media={w.officerPhoto}
              fallbackLabel={w.officer.name}
              aspect="1/1"
              className="sw-officer-photo"
              sizes="52px"
            />
          )}
          <span className="sw-officer-label">Programme officer</span>
          <span className="sw-officer-name">{w.officer.name}</span>
          <span className="sw-officer-role">{w.officer.role}</span>
        </div>
      </div>

      <section className="section">
        <div className="container container-narrow">
          {w.vision && (
            <>
              <div className="kicker">Vision</div>
              <p className="lede" style={{ marginTop: 12 }}>
                {w.vision}
              </p>
            </>
          )}

          <div className="kicker" style={{ marginTop: w.vision ? 48 : 0 }}>
            Mission
          </div>
          <ul className="detail-list" style={{ marginTop: 12 }}>
            {w.mission.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>

          {w.notes && (
            <div className="sw-notes">
              {w.notes.map((n) => (
                <div key={n.title} className="sw-note">
                  <h3>{n.title}</h3>
                  {n.body.map((b, i) => (
                    <p key={i}>{b}</p>
                  ))}
                </div>
              ))}
            </div>
          )}

          {w.slug === "red-cross" && (
            <div style={{ marginTop: 48 }}>
              <div className="kicker">The seven fundamental principles</div>
              <div className="sw-principles">
                {RED_CROSS_PRINCIPLES.map((p) => (
                  <div key={p.name} className="sw-principle">
                    <div className="sw-principle-name">{p.name}</div>
                    <div className="sw-principle-gloss">{p.gloss}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {rest.length > 0 && (
        <section className="section section--tight">
          <div className="container">
            <div className="kicker">In the field</div>
            <div className="sw-gallery">
              {rest.map((p) => (
                <Figure
                  key={p.url}
                  media={p}
                  fallbackLabel={w.abbr}
                  aspect="3/2"
                  sizes="(max-width: 700px) 100vw, 30vw"
                />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="container">
          <div className="section-head">
            <div>
              <div className="kicker">Also on campus</div>
              <h2>The other service wings</h2>
            </div>
            <Link className="btn-link" to={ROUTES.campusLife}>
              All of campus life <span className="arr">→</span>
            </Link>
          </div>
          <div className="sw-siblings">
            {siblings.map((s) => (
              <ServiceWingCard key={s.slug} w={s} />
            ))}
          </div>
        </div>
      </section>

      <div className="container" style={{ paddingBottom: 48 }}>
        <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Source:{" "}
          <a href={w.source} target="_blank" rel="noreferrer">
            {w.source}
          </a>
        </p>
      </div>
    </article>
  );
}
